import { useState } from 'react';

/**
 * Non-intrusive "new activity" affordance for paginated roster tabs.
 *
 * The BE sorts roster rows by recency, so a fresh upload / message / status
 * change bubbles a row to the TOP (page 1). A viewer sitting on page 2+ would
 * otherwise miss it. Rather than yank them back to page 1 (jarring), this pill
 * appears when the CURRENT page's list refetches in the background — i.e. an
 * SSE-driven update, which surfaces as `isRefetching` (true), distinct from
 * pagination / first load (which are `isLoading`). The viewer chooses to jump.
 *
 * Self-contained: own show/hide state, resets on page change.
 */
export function JumpToTopPill({
  isRefetching,
  page,
  onJump,
}: {
  isRefetching: boolean;
  page: number;
  onJump: () => void;
}) {
  // `show` is a latch: it turns on when the current page (page > 1) refetches in
  // the background, and resets on page change (the jump itself, or manual
  // pagination). Tracked during render via prev-value comparison — the
  // React-sanctioned "adjust state on prop change" pattern — so there's no
  // effect and no extra committed paint.
  const [show, setShow] = useState(() => page > 1 && isRefetching);
  const [prevPage, setPrevPage] = useState(page);
  const [prevRefetching, setPrevRefetching] = useState(isRefetching);
  if (page !== prevPage) {
    setPrevPage(page);
    setShow(page > 1 && isRefetching);
  }
  if (isRefetching !== prevRefetching) {
    setPrevRefetching(isRefetching);
    if (page > 1 && isRefetching) setShow(true);
  }

  if (!show || page <= 1) return null;

  return (
    <div className="flex justify-center py-1">
      <button
        type="button"
        onClick={() => {
          setShow(false);
          onJump();
        }}
        className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-caption-medium text-violet-700 transition-colors hover:bg-violet-100"
      >
        <span aria-hidden>↑</span> New activity — jump to top
      </button>
    </div>
  );
}
